import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  Host,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, ReplaySubject, Subject } from 'rxjs';

@Directive({
  selector: 'ng-template[bottomSheetContent]',
})
export class ContentTemplate {
  constructor(public templateRef: TemplateRef<any>) { }
}

const closedStateStyle = style({
  transform: 'translateY(100%)',
});

const transitionToggleOpenedAnim = animate('300ms ease-in-out');

@Component({
  selector: 'app-bottom-sheet',
  templateUrl: 'bottom-sheet.component.html',
  styleUrls: ['bottom-sheet.component.scss'],
  animations: [
    trigger('handleOpened', [
      state(
        'true',
        style({
          transform: 'translateY(0%)',
        })
      ),
      state('false', closedStateStyle),
      transition('void => true', [
        closedStateStyle,
        transitionToggleOpenedAnim,
      ]),
      transition('false => true', transitionToggleOpenedAnim),
      transition('* => false', transitionToggleOpenedAnim),
    ]),
  ],
})
export class BottomSheetComponent implements OnInit, OnChanges, OnDestroy {
  @ContentChild(ContentTemplate, { static: false })
  contentTpl: ContentTemplate;

  @ViewChild('btnOpenBottomSheetEl', { static: false })
  btnOpenBottomSheetElRef: ElementRef<HTMLDivElement>;

  @ViewChild('bottomSheetContainerEl', { static: false })
  bottomSheetContainerElRef: ElementRef<HTMLDivElement>;

  @Input()
  opened = false;
  @Output()
  openedChange = new EventEmitter<boolean>();

  @Output()
  onOpening = new EventEmitter<any>(true);
  @Output()
  onOpened = new EventEmitter<any>(true);
  @Output()
  onClosing = new EventEmitter<any>(true);
  @Output()
  onClosed = new EventEmitter<any>(true);

  initialized = false;

  openedState = false;

  openedInternal = false;
  opening = false;
  closing = false;
  closed = true;

  @Input()
  overlayMask = true;

  @Input()
  container: HTMLElement;

  @Input()
  sheetTitle: string;

  @Input()
  showHeader: boolean = true;
  @Input()
  showHeaderDivider: boolean = false;

  @Input()
  height: string = 'auto';
  @Input()
  maxHeight: string = 'auto';

  @Input()
  minHeight: string = '15rem';

  @Input()
  minimizeBtnEnabled = true;

  @Input()
  minimized = false;

  @Output()
  minimizedChange = new EventEmitter<boolean>();

  @Output()
  onContentReady = new EventEmitter<void>();

  private _contentReadySubject = new ReplaySubject<void>(1);

  private lifecycle = new Subject<void>();

  private _minimizeInternalChangedSubject = new BehaviorSubject<boolean>(
    !!this.minimized
  ).pipe(distinctUntilChanged()) as Subject<boolean>;

  constructor(
    @Host()
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private changeDetector: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.openedInternal = this.opened;
    this.closed = !this.opened;

    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.opened) {
      this.openedState = this.opened;
    }

    if (changes.minimized) {
      this._minimizeInternalChangedSubject.next(!!this.minimized);
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onTriggerEventStart(e) {
    if (!this.initialized) {
      return;
    }

    this.opening =
      (e.fromState === false || e.fromState === 'void') && e.toState === true;
    this.closing = !this.opening;

    this.openedInternal = false;
    this.closed = false;

    this.syncHostStateClass();
    this.changeDetector.detectChanges();

    if (this.opening) {
      this.onOpening.emit();
    } else if (this.closing) {
      this.onClosing.emit();
    }
  }

  onTriggerEventDone(e) {
    if (!this.opening && !this.closing) {
      return;
    }

    if (
      (this.opening && !this.openedState) ||
      (this.closing && this.openedState)
    ) {
      return;
    }

    this.openedInternal = this.opening;

    const shouldEmitOpenedChanges = this.openedInternal !== this.opened;

    this.opened = this.openedInternal;
    this.closed = !this.openedInternal;

    if (this.opening) {
      this.opening = false;
      this.syncHostStateClass();
      this.changeDetector.detectChanges();

      if (shouldEmitOpenedChanges) {
        this.openedChange.emit(this.opened);
      }

      this.onOpened.emit();
    } else if (this.closing) {
      this.closing = false;
      this.syncHostStateClass();
      this.changeDetector.detectChanges();

      if (shouldEmitOpenedChanges) {
        this.openedChange.emit(this.opened);
      }

      this.onClosed.emit();
    }
  }

  onContentReadyInternal() {
    this._contentReadySubject.next();

    this.onContentReady.emit();
  }

  onBtnMinimizeToggleClick(e) {
    this.minimized = !this.minimized;
    this._minimizeInternalChangedSubject.next(!!this.minimized);
    this.minimizedChange.emit(this.minimized);
  }

  onBtnCloseClick() {
    this.openedState = false;
  }

  private syncHostStateClass() {
    const aux: [boolean, string][] = [
      [this.openedInternal, 'state-opened'],
      [this.opening, 'state-opening'],
      [this.closing, 'state-closing'],
      [this.closed, 'state-closed'],
    ];

    aux.forEach(([value, className]) => {
      if (value) {
        this.renderer.addClass(this.elementRef.nativeElement, className);
      } else {
        this.renderer.removeClass(this.elementRef.nativeElement, className);
      }
    });
  }
}
